import { observable, computed, action, makeObservable, reaction } from 'mobx';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';

export type ComplianceAuditTabIdType = 'affordabilityAudit' | 'selfExclusionAudit' | 'gamstopAudit';

const COMPLIANCE_TAB_IDS: ComplianceAuditTabIdType[] = ['affordabilityAudit', 'selfExclusionAudit', 'gamstopAudit'];

export const isComplianceAuditTabId = (value: string): value is ComplianceAuditTabIdType => {
    return COMPLIANCE_TAB_IDS.some((id) => id === value);
};

export class ComplianceAuditsState {
    @observable public openTabs: Set<ComplianceAuditTabIdType> = new Set();
    private readonly disposers: Array<() => void> = [];

    public constructor(private readonly routerBackoffice: RouterBackoffice) {
        makeObservable(this);

        this.disposers.push(
            reaction(
                () => this.routerBackoffice.getValueString('customers'),
                () => {
                    this.openTabs.clear();
                }
            )
        );

        this.disposers.push(
            reaction(
                () => this.routerBackoffice.getParametersString('customers', 'audits'),
                (param) => {
                    if (param !== null && isComplianceAuditTabId(param)) {
                        this.openTabs.add(param);
                    }
                },
                { fireImmediately: true }
            )
        );
    }

    public dispose = (): void => {
        for (const disposer of this.disposers) {
            disposer();
        }
    };

    @computed public get activeTab(): ComplianceAuditTabIdType | null {
        const param = this.routerBackoffice.getParametersString('customers', 'audits');
        if (param !== null && isComplianceAuditTabId(param)) {
            return param;
        }
        return null;
    }

    @computed public get isVisible(): boolean {
        return this.activeTab !== null && this.openTabs.size > 0;
    }

    @computed public get orderedTabs(): ComplianceAuditTabIdType[] {
        return COMPLIANCE_TAB_IDS.filter((tabId) => this.openTabs.has(tabId));
    }

    @action public switchTab = (tabId: ComplianceAuditTabIdType): void => {
        this.routerBackoffice.setParamInNamespace('customers', 'audits', tabId);
    };

    @action public closeTab = (tabId: ComplianceAuditTabIdType): void => {
        this.openTabs.delete(tabId);

        if (this.openTabs.size > 0) {
            const remaining = this.orderedTabs;
            const firstTab = remaining[0];
            if (firstTab !== undefined) {
                this.switchTab(firstTab);
            }
        } else {
            this.routerBackoffice.removeParamInNamespace('customers', 'audits');
        }
    };

    @action public minimize = (): void => {
        this.routerBackoffice.removeParamInNamespace('customers', 'audits');
    };
}
